import React, { ReactElement, useContext } from 'react'
import { useSelector } from 'react-redux'
import {
    Drawer, Form, notification, Select, Space, Button, Input, DatePicker, ConfigProvider
} from 'antd'
import locale from 'antd/es/date-picker/locale/zh_CN'
import zhCN from 'antd/es/locale/zh_CN'
import moment from 'moment'
import type { IAppState } from '@/store/reducers'
import { SmileOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { DrawerShowContext } from '@/pages/techReviewProgress'
import { ECoreTechnology, ETechnologyContribution, ETechnologyLeadership } from '@/types'

export interface ISearchData {
    coreTechnology?: number;
    endDueDate?: [moment.Moment, moment.Moment];
    isCross?: boolean;
    isDisruptive?: boolean;
    proposalName?: string;
    creator?: string;
    ptn?: string;
    reviewResult?: boolean;
    technologyContribution?: number;
    technologyLeadership?: number;
    workGroupId?: number;
}

function ReviewSearchPop(): ReactElement {
    const { t, i18n } = useTranslation()
    const { showSearchPop, setShowSearchPop, closeCallback, formData } = useContext(DrawerShowContext)
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)
    const layout = {
        labelCol: { span: 8 },
        wrapperCol: { span: 16 },
    }
    const tailLayout = {
        wrapperCol: { offset: 8, span: 10 },
    }

    const onClose = () => {
        setShowSearchPop(false)
    }

    // 封装拼接字符串的函数
    const joinTranslatedStrings = (...translations: string[]) => {
        return translations.join(i18n.language !== 'zh' ? ' ' : '')
    }

    const LEADER_ARR = [
        {
            label: t('leadership'),
            id: ETechnologyLeadership.LEADING,
        },
        // {
        //     label: t('homeleadership'),
        //     id: ETechnologyLeadership.HOME_LEADING,
        // },
        {
            label: t('equalize'),
            id: ETechnologyLeadership.EQUAL,
        },
        {
            label: t('trail'),
            id: ETechnologyLeadership.TRAIL,
        },
    ]

    const CONTRIBUTION_ARR = [
        {
            label: t('innovationSellingPoint'),
            id: ETechnologyContribution.INNOVATION,
        },
        {
            label: t('keySellingPoint'),
            id: ETechnologyContribution.KEY,
        },
        {
            label: t('secondarySellingPoints'),
            id: ETechnologyContribution.SECONDARY,
        },
        {
            label: t('infrastructure'),
            id: ETechnologyContribution.BASIC,
        },
    ]

    const TECH_TYPE_ARR = [
        {
            label: joinTranslatedStrings(t('core'), t('technology')),
            id: ECoreTechnology.CORE,
        },
        {
            label: joinTranslatedStrings(t('key'), t('technology')),
            id: ECoreTechnology.KEY,
        },
        {
            label: t('basicTechnology'),
            id: ECoreTechnology.BASIC,
        },
        {
            label: t('reserveTechnology'),
            id: ECoreTechnology.RESERVE,
        },
    ]

    if (!showSearchPop) {
        return (
            <ConfigProvider locale={i18n.language === 'zh' ? zhCN : undefined}>
                <Drawer title={t('search') + (i18n.language !== 'zh' ? ' ' : '') + t('condition')} placement="right"
                    width={600}
                    onClose={onClose} visible={showSearchPop}
                />
            </ConfigProvider>
        )
    }

    return <SearchForm 
        layout={layout}
        tailLayout={tailLayout}
        onClose={onClose}
        closeCallback={closeCallback}
        formData={formData}
        workgroupList={workgroupList}
        LEADER_ARR={LEADER_ARR}
        CONTRIBUTION_ARR={CONTRIBUTION_ARR}
        TECH_TYPE_ARR={TECH_TYPE_ARR}
        joinTranslatedStrings={joinTranslatedStrings}
        t={t}
        i18n={i18n}
        showSearchPop={showSearchPop}
    />
}

// 分离的表单组件
function SearchForm({ 
    layout, 
    tailLayout, 
    onClose, 
    closeCallback, 
    formData, 
    workgroupList, 
    LEADER_ARR, 
    CONTRIBUTION_ARR, 
    TECH_TYPE_ARR, 
    joinTranslatedStrings, 
    t, 
    i18n, 
    showSearchPop 
}: any) {
    const [form] = Form.useForm<ISearchData>()
    const { Option } = Select
    const { RangePicker } = DatePicker
    
    React.useEffect(() => {
        if (formData && Object.keys(formData).length > 0) {
            form.setFieldsValue(formData)
        }
    }, [formData, form])

    const onFinish = async (values: ISearchData) => {
        console.log(values, 'search form values')
        const submitValues = {
            ...values,
            startDueDate: values.endDueDate?.[0]?.format('YYYY-MM-DD'),
            endDueDate: values.endDueDate?.[1]?.format('YYYY-MM-DD'),
        }
        closeCallback(submitValues)
        onClose()
    }
    
    const onFinishFailed = ({ errorFields }: { errorFields: any }) => {
        if (errorFields.length) {
            const args = {
                message: t('FinishFailed'),
                description: '',
                icon: <SmileOutlined style={{ color: '#e1931e' }} />,
                duration: 3,
            }
            notification.open(args)
        }
    }
    
    const onReset = () => {
        form.resetFields()
    }

    return (
        <ConfigProvider locale={i18n.language === 'zh' ? zhCN : undefined}>
            <Drawer title={t('search') + (i18n.language !== 'zh' ? ' ' : '') + t('condition')} placement="right"
                width={600}
                onClose={onClose} visible={showSearchPop}
            >
                <Form {...layout} form={form}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    className='ti_an_detail'
                >
                    <Form.Item name="workGroupId" label={<span className='label'>{t('Work Group')}</span>}>
                        <Select allowClear placeholder={t('Work Group Placeholder')}>
                            {
                                workgroupList.map((item: any) => {
                                    return <Option key={item.id} value={item.id}>{item.groupName}</Option>
                                })
                            }
                        </Select>
                    </Form.Item>
                    <Form.Item name="proposalName" label={<span className='label'>{t('proposalName')}</span>}>
                        <Input placeholder={t('Please Enter') + (i18n.language !== 'zh' ? ' ' : '') + t('proposalName')} />
                    </Form.Item>
                    <Form.Item name="ptn" label={<span className='label'>{t('PTN')}</span>}>
                        <Input placeholder={t('Please Enter') + (i18n.language !== 'zh' ? ' ' : '') + t('PTN')} />
                    </Form.Item>
                    <Form.Item name="creator" label={<span className='label'>{t('creator')}</span>}>
                        <Input placeholder={t('Please Enter') + (i18n.language !== 'zh' ? ' ' : '') + t('creator')} />
                    </Form.Item>

                    {/* 技术领先性 */}
                    <Form.Item name="technologyLeadership" label={<span className='label'>{t('technologicalLeadership')}</span>}>
                        <Select placeholder={joinTranslatedStrings(t('Please Select'), t('technologicalLeadership'))}
                            allowClear
                        >
                            {LEADER_ARR.map((i: any) => <Option  key={i.id} value={i.id}>{i.label}</Option>)}
                        </Select>
                    </Form.Item>
                    {/* 技术贡献性 */}
                    <Form.Item name="technologyContribution" label={<span className='label'>{t('technicalContribution')}</span>}>
                        <Select placeholder={joinTranslatedStrings(t('Please Select'), t('technicalContribution'))}
                            allowClear
                        >
                            {CONTRIBUTION_ARR.map((i: any) => <Option  key={i.id} value={i.id}>{i.label}</Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item name="coreTechnology" label={<span className='label'>{t('techType')}</span>}>
                        <Select placeholder={joinTranslatedStrings(t('Please Select'), t('techType'))}
                            allowClear
                        >
                            {TECH_TYPE_ARR.map((i: any) => <Option key={i.id} value={i.id}>{i.label}</Option>)}
                        </Select>
                    </Form.Item>

                    <Form.Item name="isCross" label={<span className='label'>{t('isCross')}</span>}>
                        <Select placeholder={joinTranslatedStrings(t('Please Select') , t('isCross'))}
                            allowClear
                        >
                            <Option value={null}>{t('All')}</Option>
                            <Option value={true}>{t('Yes')}</Option>
                            <Option value={false}>{t('No')}</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="isDisruptive" label={<span className='label'>{t('isDisruptive')}</span>}>
                        <Select placeholder={t('Please Select') + (i18n.language !== 'zh' ? ' ' : '') + t('isDisruptive')}
                            allowClear
                        >
                            <Option value={null}>{t('All')}</Option>
                            <Option value={true}>{t('Yes')}</Option>
                            <Option value={false}>{t('No')}</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="reviewResult" label={<span className='label'>{t('reviewResult')}</span>}>
                        <Select placeholder={t('Please Select') + (i18n.language !== 'zh' ? ' ' : '') + t('reviewResult')}
                            allowClear
                        >
                            <Option value={null}>{t('reviewResultNull')}</Option>
                            <Option value={true}>{t('reviewResultTrue')}</Option>
                            <Option value={false}>{t('reviewResultFalse')}</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="endDueDate" label={<span className='label'>
                        {t('end') + (i18n.language !== 'zh' ? ' ' : '') + t('date') + (i18n.language !== 'zh' ? ' ' : '') + t('range')}
                    </span>}>
                        <RangePicker allowClear
                            locale={i18n.language === 'zh' ? locale : undefined}
                            placeholder={[t('Please Select') + (i18n.language !== 'zh' ? ' ' : '') + t('start') + (i18n.language !== 'zh' ? ' ' : '') + t('date'),
                                t('Please Select') + (i18n.language !== 'zh' ? ' ' : '') + t('end') + (i18n.language !== 'zh' ? ' ' : '') + t('date')]}
                        />
                    </Form.Item>
                    <Form.Item {...tailLayout} style={{ textAlign: 'center' }}>
                        <Space size={20}>
                            <Button type="primary" htmlType="submit">
                                {t('search')}
                            </Button>
                            <Button htmlType="button" onClick={onReset}>
                                {t('reset')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Drawer>
        </ConfigProvider>
    )
}

export default ReviewSearchPop
